<template>
  <modal
    id="leave-review"
    name="leave-review"
    scrollable
    width="100%"
    height="auto"
    :clickToClose="false"
    @closed="handleClose"
  >
  <div class="leave-review-modal">
  <div class="leave-review-modal__header">
    <div class="leave-review-modal__header-bar" />
    <div class="leave-review-modal__header-close" @click="handleClose">
      <span class="leave-review-modal__header-close-icon fa fa-close" />
    </div>
  </div>
  <div class="leave-review-modal__body">
    <div class="leave-review-modal__body-title">
      <div class="leave-review-modal__body-title-main">
        How did we do?
      </div>
      <div class="leave-review-modal__body-title-secondary">
        Your feedback is very important to us!
      </div>
    </div>
    <leave-review-stars-input v-model="rating" />
    <div class="leave-review-modal__body-input-group">
      <label class="leave-review-modal__body-input-label">
        Leave feedback
      </label>
      <textarea
        v-model="description"
        class="leave-review-modal__body-input-textarea"
        :rows="6"
        :disabled="!rating"
      />
    </div>
    <div class="leave-review-modal__body-input-group">
      <label class="leave-review-modal__body-input-label">
        Review as
      </label>
      <leave-review-reviewers-select
        v-model="reviewerUserId"
        :inspection="inspection"
      />
    </div>

  </div>
  <div class="leave-review-modal__footer">
    <div class="leave-review-modal__footer-btn">
      <button class="leave-review-modal__footer-btn--secondary" @click="handleClearAll">
        Clear All
      </button>
    </div>
    <div class="leave-review-modal__footer-btn">
      <button :disabled="!rating || saving" class="leave-review-modal__footer-btn--primary" @click="handleSave">
        Leave Feedback
      </button>
    </div>
  </div>
</div>
  </modal>
</template>

<script>
import { mapState } from 'vuex'
import axios from '@/AxiosService'
import LeaveReviewStarsInput from './LeaveReviewStarsInput.vue'
import LeaveReviewReviewersSelect from './LeaveReviewReviewersSelect.vue'

export default {
  name: 'LeaveReview',

  components: {
    LeaveReviewStarsInput,
    LeaveReviewReviewersSelect,
  },

  props: {
    reviewer: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      rating: 0,
      reviewerUserId: null,
      description: '',
      saving: false,
    }
  },

  computed: {
    ...mapState('reports', ['inspection']),
  },

  beforeDestroy() {
    this.handleClose()
  },

  mounted() {
    this.reviewerUserId = this.reviewer.attributes?.user_id
    this.$modal.show('leave-review')
  },

  methods: {
    handleClose() {
      this.$modal.hide('leave-review')
      this.$emit('close')
    },

    handleClearAll() {
      this.rating = 0
      this.description = ''
    },

    async handleSave() {
      try {
        this.saving = true

        const data = {
          attributes: {
            id_token: this.$route.query.id_token,
            inspection_slug: this.inspection.attributes.slug,
            reviewed_from: 'Report Popup',
            on_behalf_of_user: this.reviewerUserId,
            rating: this.rating,
            description: this.description,
          },
        }

        await axios.post('/api/v2/public/inspection_reviews', { data })

        this.$store.dispatch('notifier/show', {
          type: 'Saved',
          msg: 'Thanks for your feedback!!',
        })

        this.handleClose()
      } catch (error) {
        this.$store.dispatch('notifier/show', {
          type: 'Error',
          msg: 'There was an error leaving your review!',
        })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style lang="scss">
$background-color-secondary: #ebedef;
$color-primary: #1771b8;
$color-disabled: #9aa5b1;
$font-size-label: 14px;

.leave-review-modal {
  font-family: Inter;
  display: flex;
  flex-direction: column;
  max-width: 800px;
  margin: 100px auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px -2px rgba(55, 65, 81, 0.03);

  &__header {
    width: 100%;
    height: 70px;
    background: white;
    display: flex;
    flex-direction: column;
    border-radius: 8px 8px 0 0;

    &-bar {
      top: 0;
      left: 0;
      height: 14px;
      background: $color-primary;
      border-radius: 8px 8px 0 0;
    }

    &-close {
      align-items: center;
      cursor: pointer;
      display: flex;
      flex: 1;
      justify-content: flex-end;
      padding-right: 16px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 36px;
    padding: 0 80px 82px;
    background: white;
    width: 100%;

    &-title {
      display: inline-flex;
      flex-direction: column;
      gap: 8px;

      &-main,
      &-secondary {
        align-self: stretch;
        color: #212731;
        word-wrap: break-word;
      }

      &-main {
        font-size: 24px;
        font-weight: 700;
      }

      &-secondary {
        font-size: 16px;
        font-weight: 400;
      }
    }

    &-input-group {
      display: inline-flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      gap: 8px;
    }

    &-input-label {
      align-self: stretch;
      color: #374151;
      font-size: $font-size-label;
      font-weight: 600;
      word-wrap: break-word;
    }

    &-input-textarea {
      font-size: 14px;
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      background: white;

      &:disabled {
        background-color: $background-color-secondary;
      }
    }

  }

  &__footer {
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
    padding: 14px 32px;
    align-self: stretch;
    background: white;
    border-top: 1px solid #e5e7eb;
    border-radius: 0 0 8px 8px;

    &-btn {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 4px;

      &--primary {
        padding: 12px 16px;
        font-size: $font-size-label;
        font-weight: 600;
        text-align: center;
        border-radius: 0.25rem;
        color: white;
        background-color: $color-primary;
        word-wrap: break-word;

        &:hover {
          background-color: #1c5887;
        }

        &:disabled {
          background-color: $background-color-secondary;
          color: $color-disabled;
          pointer-events: none;
        }
      }

      &--secondary {
        padding: 12px 16px;
        font-size: $font-size-label;
        font-weight: 600;
        text-align: center;
        border-radius: 0.25rem;
        color: $color-primary;
        word-wrap: break-word;

        &:hover,
        &:focus {
          background-color: #f1f8fd;
        }

        &:disabled {
          color: $color-disabled;
        }
      }
    }
  }
}
</style>

<style>
#leave-review .v--modal-box.v--modal {
  background: transparent;
  box-shadow: none;
}
#leave-review .v-list a.v-list__tile.v-list__tile--link {
  height: auto;
  min-height: 5em;
}
#leave-review .v-input__append-inner {
  margin-top: auto;
}
</style>